import React from "react";

import {
    BaseComponentProps,
    createComponentBase,
} from "../../../../libs/components";
import { useOktaUserListQuery } from "../../../../store/okta/userSlice";
import { Dropdown } from "../../../atoms/dropdown";
import { Input } from "../../../atoms/input";
import { CreatableDropdown } from "../../../atoms/creatableDropdown";
import { DESIGNATION_OPTIONS, PERMISSIONS } from "../../../../constants/constants";
import { useMitutoyoDepartmentListQuery, useMitutoyoSectionListQuery, useOrganizationOptionsQuery, useProjectListQuery } from "../../../../store/organization/organizationSlice";
import { hasPermission } from "../../../../utils";

export interface UserProfileUpdateRequestProps extends BaseComponentProps {
    payload: any;
    setPayload: any;
    setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const UserProfileUpdateRequest = ({
    className,
    payload,
    setPayload,
    setLoading,
}: UserProfileUpdateRequestProps) => {
    const componentClassName = "o-userProfileUpdateRequestProps";
    const { Container } = createComponentBase(componentClassName, "div");
    const modifiers = {};

    const [sectionOptions, setSectionOptions] = React.useState<any>([]);
    const [departmentOptions, setDepartmentOptions] = React.useState<any>([]);

    const { data: users, isLoading: usersLoading } = useOktaUserListQuery({});
    const { data: organizations, isLoading: organizationsLoading } = useOrganizationOptionsQuery();
    const { data: projects, isLoading: projectsLoading } = useProjectListQuery({});

    React.useEffect(() => {
        if (setLoading) {
            setLoading(usersLoading || organizationsLoading || projectsLoading);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usersLoading, organizationsLoading, projectsLoading]);

    const userChangeHandler = (val: any) => {
        const user = users?.result?.data?.find((user: any) => user.email === val.value);

        setPayload({
            ...payload,
            user: {
                value: val,
                isError: false,
                error: "",
            },
            first_name: {
                value: user?.firstName,
                isError: false,
                error: "",
            },
            last_name: {
                value: user?.lastName,
                isError: false,
                error: "",
            },
            email: {
                value: user?.email,
                isError: false,
                error: "",
            },
            designation: {
                value: { label: user?.designation, value: user?.designation },
                isError: false,
                error: "",
            },
            organization: {
                value: { label: user?.organization?.name, value: user?.organization?.id },
                isError: false,
                error: "",
            },
            department: {
                value: { label: user?.department?.department, value: user?.department?.department },
                isError: false,
                error: "",
            },
            section: {
                value: { label: user?.section?.section, value: user?.section?.section },
                isError: false,
                error: "",
            },
            location: {
                value: { label: user?.location, value: user?.location },
                isError: false,
                error: "",
            },
            projects: {
                value: user?.projects?.map((project: any) => ({
                    label: project?.name,
                    value: project?.name,
                })),
                isError: false,
                error: "",
            },
            pic: {
                value: { label: user?.pic?.firstName + " " + user?.pic?.lastName, value: user?.pic?.email },
                isError: false,
                error: "",
            },
        });
    }

    const getSectionOptions = (data: any[], organization: string) => {
        const seen = new Set<string>();
        const options = data
            ?.filter(item => item.organization === organization)
            .map(item => item.name)
            .filter(name => {
                if (seen.has(name)) return false;
                seen.add(name);
                return true;
            })
            .map(name => ({ value: name, label: name }));

        const finalOptions = options.length > 0 ? options : [{ value: 'Unknown', label: 'Unknown' }];
        setSectionOptions(finalOptions);
    };

    const getDepartmentOptions = (data: any[], organization: string) => {
        const seen = new Set<string>();
        const options = data
            ?.filter(item => item.organization === organization)
            .map(item => item.name)
            .filter(name => {
                if (seen.has(name)) return false;
                seen.add(name);
                return true;
            })
            .map(name => ({ value: name, label: name }));

        const finalOptions = options.length > 0 ? options : [{ value: 'Unknown', label: 'Unknown' }];
        setDepartmentOptions(finalOptions);
    };

    return (
        <Container className={className} modifiers={modifiers}>
            <div className={`${componentClassName}__form`}>
                <div className={`${componentClassName}__form__item`}>
                    <label className={`${componentClassName}__form__label`}>User</label>
                    <Dropdown
                        className={`${componentClassName}__form__item__dropdown`}
                        options={users?.result?.data?.map((user: any) => ({
                            label: user?.firstName + " " + user?.lastName,
                            value: user?.email,
                        }))}
                        selected={payload?.user?.value}
                        placeholder="Select a user"
                        onClickChange={userChangeHandler}
                        isRequired
                    />
                </div>

                <Input
                    value={payload?.first_name?.value}
                    label="First Name"
                    type="text"
                    placeholder="Enter email first name"
                    className={`${componentClassName}__form__item__input`}
                    onChange={(e) =>
                        setPayload({
                            ...payload,
                            first_name: {
                                value: e.target.value,
                                isError: false,
                                error: "",
                            },
                        })
                    }
                    isRequired
                    isStar
                />

                <Input
                    value={payload?.last_name?.value}
                    label="Last Name"
                    type="text"
                    placeholder="Enter email last name"
                    className={`${componentClassName}__form__item__input`}
                    onChange={(e) =>
                        setPayload({
                            ...payload,
                            last_name: {
                                value: e.target.value,
                                isError: false,
                                error: "",
                            },
                        })
                    }
                    isRequired
                    isStar
                />


                <Input
                    value={payload?.email?.value}
                    label="Email"
                    type="text"
                    placeholder="Enter email address"
                    className={`${componentClassName}__form__item__input`}
                    onChange={() => { }}
                    readonly
                    isRequired
                    isStar
                />

                <div className={`${componentClassName}__form__item`}>
                    <label className={`${componentClassName}__form__item__label`}>
                        Designation
                    </label>
                    <CreatableDropdown
                        placeholder="Select/Create designation"
                        options={DESIGNATION_OPTIONS}
                        selected={payload?.designation.value}
                        onClickChange={(val) =>
                            setPayload({
                                ...payload,
                                designation: {
                                    value: val,
                                    isError: false,
                                    error: "",
                                },
                            })
                        }
                        isRequired
                    />
                </div>

                <div className={`${componentClassName}__form__item`}>
                    <label className={`${componentClassName}__form__item__label`}>
                        Organization
                    </label>
                    <Dropdown
                        placeholder="Select organization"
                        options={organizations?.result?.organizationOptions}
                        selected={payload?.organization?.value}
                        onClickChange={(val) =>
                            setPayload({
                                ...payload,
                                organization: {
                                    value: val,
                                    isError: false,
                                    error: "",
                                },
                                department: {
                                    value: { label: "", value: "" },
                                    isError: false,
                                    error: "",
                                },
                                section: {
                                    value: { label: "", value: "" },
                                    isError: false,
                                    error: "",
                                },
                            })
                        }
                        isRequired
                    />
                </div>

                <div className={`${componentClassName}__form__item`}>
                    <label className={`${componentClassName}__form__item__label`}>
                        Department
                    </label>
                    <Dropdown
                        placeholder="Select department"
                        options={organizations?.result?.departmentOptions}
                        selected={payload?.department.value}
                        onClickChange={(val) =>
                            setPayload({
                                ...payload,
                                department: {
                                    value: val,
                                    isError: false,
                                    error: "",
                                },
                            })
                        }
                        isRequired
                        //focusHandler={() => getDepartmentOptions(departmentList?.result?.data, payload?.organization?.value?.label)}
                    />
                </div>

                <div className={`${componentClassName}__form__item`}>
                    <label className={`${componentClassName}__form__item__label`}>
                        Section
                    </label>
                    <Dropdown
                        placeholder="Select section"
                        options={organizations?.result?.sectionOptions}
                        selected={payload?.section.value}
                        onClickChange={(val) =>
                            setPayload({
                                ...payload,
                                section: {
                                    value: val,
                                    isError: false,
                                    error: "",
                                },
                            })
                        }
                        isRequired
                       // focusHandler={() => getSectionOptions(sectionList?.result?.data, payload?.organization?.value?.label)}
                    />
                </div>

                <div className={`${componentClassName}__form__item`}>
                    <label className={`${componentClassName}__form__item__label`}>
                        Location
                    </label>
                    <Dropdown
                        placeholder="Select/Create designation"
                        options={organizations?.result?.locationOptions}
                        selected={payload?.location?.value}
                        onClickChange={(val) =>
                            setPayload({
                                ...payload,
                                location: {
                                    value: val,
                                    isError: false,
                                    error: "",
                                },
                            })
                        }
                        isRequired
                    />
                </div>

                <div className={`${componentClassName}__form__item`}>
                    <label className={`${componentClassName}__form__item__label`}>
                        Projects
                    </label>
                    <Dropdown
                        isMulti
                        placeholder="Select projects"
                        options={projects?.result?.data?.map((item: any) => ({
                            label: item?.name,
                            value: item?.name,
                        }))}
                        selected={payload?.projects?.value}
                        onClickChange={(val) =>
                            setPayload({
                                ...payload,
                                projects: {
                                    value: val,
                                    isError: false,
                                    error: "",
                                },
                            })
                        }
                        isRequired
                    />
                </div>

                <div className={`${componentClassName}__form__item`}>
                    <label className={`${componentClassName}__form__label`}>PIC/Personal In Charge/Manager</label>
                    <Dropdown
                        className={`${componentClassName}__form__item__dropdown`}
                        options={users?.result?.data?.map((user: any) => ({
                            label: user?.firstName + " " + user?.lastName,
                            value: user?.email,
                        }))}
                        selected={payload?.pic?.value}
                        placeholder="Select saas tool"
                        onClickChange={(val) =>
                            setPayload({
                                ...payload,
                                pic: {
                                    value: val,
                                    isError: false,
                                    error: "",
                                },
                            })
                        }
                        isRequired
                        isDisabled={!hasPermission(PERMISSIONS.REQUEST_MANAGEMENT_USER_PROFILE_UPDATE_REQUEST.title)}
                    />
                </div>

            </div>
        </Container>
    )
};